<template>
  <div class="padding-16">
    <Switchbox
      :label="$lang.status"
      :value="form.isEnable"
      name="isEnable"
      boxClass="flex-between"
      :checkedValue="1"
      :uncheckedValue="0"
      @input="input"
    />
    <Dropdown
      :title="$lang.selectLevel"
      :label="$lang.selectLevel"
      :placeholder="$lang.selectLevel"
      :value="form.stateName"
      name="stateName"
      id="stateName"
      :vuelidateObj="$v.form.stateName"
      :options="termStateDefineOptions"
      :loading="termStateDefineLoading"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Dropdown
      :title="$lang.prerequisiteTermState"
      :label="$lang.prerequisiteTermState"
      :placeholder="$lang.prerequisiteTermState"
      :value="form.statePrerequisiteName"
      name="statePrerequisiteName"
      id="statePrerequisiteName"
      :vuelidateObj="$v.form.statePrerequisiteName"
      :options="termStateDefineOptions"
      :loading="termStateDefineLoading"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <Input
      :title="`${$lang.executeExpire} (${$lang.optional})`"
      :label="$lang.executeExpire"
      :placeholder="$lang.executeExpireDays"
      :infoTooltip="$lang.executeExpireTooltip"
      :value="form.maxDayStatusCompleted"
      name="maxDayStatusCompleted"
      id="maxDayStatusCompleted"
      type="text"
      :vuelidateObj="$v.form.maxDayStatusCompleted"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
    />
    <collapse-transition group>
      <Input
        v-if="isPayment"
        key="1"
        :title="$lang.userPaymentAmount"
        :label="$lang.userPaymentAmount"
        :placeholder="$lang.userPaymentAmount"
        :value="form.cost"
        name="cost"
        id="cost"
        type="text"
        :vuelidateObj="$v.form.cost"
        @input="input"
        @focus="resetValidation"
        @blur="checkValidation"
      />
      <Dropdown
        v-if="isPayment"
        key="2"
        :title="$lang.gatePay"
        :label="$lang.gatePay"
        :placeholder="$lang.gatePay"
        :value="form.gate"
        name="gate"
        id="gate"
        :vuelidateObj="$v.form.gate"
        :options="gateOptions"
        :loading="gateLoading"
        :asyncSearch="getGateOptions"
        @input="input"
        @focus="resetValidation"
        @blur="checkValidation"
      />
    </collapse-transition>
    <DatePicker
      :title="$lang.start_datetime"
      :label="$lang.start_datetime"
      :placeholder="$lang.start_datetime"
      :value="form.startDate"
      name="startDate"
      id="startDate"
      rightIcon="calendar-clock"
      displayFormat="HH:mm jYYYY/jMM/jDD"
      format="YYYY-MM-DD HH:mm:ss"
      type="datetime"
      :popover="true"
      :simple="true"
      :autoSubmit="true"
      :hasEraseIcon="true"
      :vuelidateObj="$v.form.startDate"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
      @erase="eraseDatePicker"
    />
    <DatePicker
      :title="$lang.end_datetime"
      :label="$lang.end_datetime"
      :placeholder="$lang.end_datetime"
      :value="form.endDate"
      name="endDate"
      id="endDate"
      rightIcon="calendar-clock"
      displayFormat="HH:mm jYYYY/jMM/jDD"
      format="YYYY-MM-DD HH:mm:ss"
      type="datetime"
      :popover="true"
      :simple="true"
      :autoSubmit="true"
      :hasEraseIcon="true"
      :vuelidateObj="$v.form.endDate"
      @input="input"
      @focus="resetValidation"
      @blur="checkValidation"
      @erase="eraseDatePicker"
    />
  </div>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import {
  Switchbox,
  Dropdown,
  Input,
  DatePicker,
} from "@/components/re-design/forms";
import { required, requiredIf, numeric } from "vuelidate/lib/validators";
import { pathOr, startsWith } from "ramda";
import {
  getGateCombo,
  getTermStateCombo,
  getTermStateDefineCombo,
} from "@/services";
import { generateError } from "@/shared/utils";

export default {
  mixins: [inputMixin],
  components: { Switchbox, Dropdown, Input, DatePicker },
  props: {
    submitted: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
  },
  validations() {
    return {
      form: {
        stateName: { required },
        statePrerequisiteName: { required },
        maxDayStatusCompleted: { numeric },
        cost: { required: requiredIf(() => this.isPayment) },
        gate: { required: requiredIf(() => this.isPayment) },
        startDate: { required },
        endDate: { required },
      },
    };
  },
  data() {
    return {
      form: {
        isEnable: 0,
        stateName: null,
        statePrerequisiteName: null,
        maxDayStatusCompleted: null,
        cost: null,
        gate: null,
        startDate: null,
        endDate: null,
      },
      termStateDefineLoading: false,
      termStateDefineOptions: [],
      gateLoading: false,
      gateOptions: [],
    };
  },
  computed: {
    isPayment() {
      const stateNameSelected = pathOr("", ["id"], this.form.stateName);
      return startsWith("payment_", stateNameSelected);
    },
  },
  watch: {
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.setParameters(value);
      },
    },
    submitted: {
      // when submitted from parent component call the submit method of parent component, because we want to handle validation
      immediate: true,
      handler(value) {
        if (value) {
          this.handleSubmitted();
        }
      },
    },
    item: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchData(value);
        }
      },
    },
  },
  mounted() {
    this.getTermStateDefineOptions();
  },
  methods: {
    eraseDatePicker(key) {
      this.input(key, null);
    },
    async getTermStateDefineOptions(text = null) {
      try {
        this.termStateDefineLoading = true;
        const response = await getTermStateDefineCombo(text);
        const { options } = response;
        this.termStateDefineOptions = options;
      } catch (error) {
        generateError(error);
      } finally {
        this.termStateDefineLoading = false;
      }
    },
    async getGateOptions(text = null) {
      try {
        this.gateLoading = true;
        const response = await getGateCombo(text);
        const { options } = response;
        this.gateOptions = [...options];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.gateLoading = false;
      }
    },
    setParameters(params) {
      /* generate parameters for parent component */
      const stateName = pathOr(null, ["stateName", "id"], params);
      const statePrerequisiteName = pathOr(
        null,
        ["statePrerequisiteName", "id"],
        params
      );
      const maxDayStatusCompleted = pathOr(
        null,
        ["maxDayStatusCompleted"],
        params
      );
      const gate = pathOr(null, ["gate", "id"], params);
      this.$emit("setParameters", {
        ...params,
        stateName,
        statePrerequisiteName,
        maxDayStatusCompleted,
        gate,
      });
    },
    handleSubmitted() {
      /* check validation and if is not error call the submit method of parent component */
      try {
        this.$v.$touch();
        if (this.$v.$anyError) return;
        this.$emit("handleSubmit");
      } catch (error) {
        //
      } finally {
        this.$emit("setSubmitted", false);
      }
    },
    fetchData(data) {
      /* fetched data when run edit action */
      this.input("isEnable", pathOr(0, ["is_enable"], data));
      this.input("stateName", {
        id: pathOr(null, ["state_name", "state_name"], data),
        caption: pathOr("", ["state_name", "state_caption"], data),
      });
      this.input("statePrerequisiteName", {
        id: pathOr(null, ["state_prerequisite_name", "state_name"], data),
        caption: pathOr("", ["state_prerequisite_name", "state_caption"], data),
      });
      this.input("gate", {
        id: pathOr(null, ["gate", "code"], data),
        caption: pathOr("", ["gate", "caption"], data),
      });
      this.input(
        "maxDayStatusCompleted",
        pathOr(null, ["max_day_status_completed"], data)
      );
      this.input("cost", pathOr(null, ["cost"], data));
      this.input("startDate", pathOr(null, ["start_date", "gregorian"], data));
      this.input("endDate", pathOr(null, ["end_date", "gregorian"], data));
    },
  },
};
</script>
